import { FC, ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Divider from "../Divider/Divider";
import { useAccordionState } from "./AccordionContext";

const ANIMATION_DURATION_IN_MS = 400;

interface AccordionPanelProps {
  children: ReactNode;
  id: string;
}

const Box = styled.div<{ css: string }>`
  overflow: hidden;
  transition: max-height ${ANIMATION_DURATION_IN_MS / 1000}s,
    opacity ${(ANIMATION_DURATION_IN_MS * 2) / 1000}s;

  ${(props) => props.css}
`;

const AccordionPanel: FC<AccordionPanelProps> = ({
  children,
  id,
}: AccordionPanelProps) => {
  const activeId = useAccordionState();
  const [maxHeight, setMaxHeight] = useState(0);
  const [hideContent, setHideContent] = useState(id !== activeId);
  const [opacity, setOpacity] = useState(id === activeId ? 1 : 0);
  const [overflow, setOverflow] = useState(
    id === activeId ? "visible" : "hidden"
  );
  const ref = useRef<HTMLDivElement>(null);

  const isOpen = id === activeId;

  useEffect(() => {
    let timeout = 0;
    let secondTimeout = 0;
    const boxHeight = ref && ref.current !== null ? getHeight(ref.current) : 0;

    if (isOpen) {
      setHideContent(false);
      // to make sure setHideContent is called before all other set states
      timeout = window.setTimeout(() => {
        setMaxHeight(boxHeight);
        setOpacity(1);
        secondTimeout = window.setTimeout(() => {
          setOverflow("visible");
        }, ANIMATION_DURATION_IN_MS);
      }, 0);
      return;
    }

    setOpacity(0);
    timeout = window.setTimeout(() => {
      setMaxHeight(0);
      setOverflow("hidden");
      secondTimeout = window.setTimeout(() => {
        setHideContent(true);
      }, ANIMATION_DURATION_IN_MS);
    }, ANIMATION_DURATION_IN_MS);

    return () => {
      clearTimeout(timeout);
      clearTimeout(secondTimeout);
    };
  }, [hideContent, isOpen]);

  return (
    <>
      <Box
        as="section"
        ref={ref}
        role="region"
        aria-labelledby={id}
        hidden={hideContent}
        css={`
          max-height: ${maxHeight}px;
          opacity: ${opacity};
          overflow: ${overflow};
        `}
      >
        {children}
      </Box>
      <Divider marginTop={0} marginBottom={0} />
    </>
  );
};

const getHeight = (element: HTMLElement) => {
  return element.scrollHeight;
};

export default AccordionPanel;
